import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RegionalPerformanceTable = ({ 
  data = [],
  isLoading = false,
  selectedCountry = 'all',
  selectedMetric = 'scans',
  selectedTimeRange = 'last7days',
  onCountrySelect,
  className = ''
}) => {
  const [sortField, setSortField] = useState('scans');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedRegion, setSelectedRegion] = useState(null);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedData = [...data]?.sort((a, b) => {
    const aValue = a?.[sortField];
    const bValue = b?.[sortField];
    
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const getSortIcon = (field) => {
    if (sortField !== field) return 'ArrowUpDown';
    return sortDirection === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  const getGrowthColor = (growth) => {
    if (growth > 0) return 'text-success';
    if (growth < 0) return 'text-error';
    return 'text-muted-foreground';
  };

  const getGrowthIcon = (growth) => {
    if (growth > 0) return 'TrendingUp';
    if (growth < 0) return 'TrendingDown';
    return 'Minus';
  };

  const formatValue = (value, type) => {
    switch (type) {
      case 'number':
        return value?.toLocaleString();
      case 'currency':
        return `€${value?.toFixed(2)}`;
      case 'percentage':
        return `${value}%`;
      default:
        return value;
    }
  };

  const MiniBarChart = ({ data, height = 20 }) => {
    if (!data || data?.length === 0) return <div className="text-xs text-muted-foreground">No data</div>;
    
    const max = Math.max(...data);
    
    return (
      <div className="flex items-end space-x-1" style={{ height }}>
        {data?.map((value, index) => (
          <div
            key={index}
            className="bg-primary/60 rounded-sm min-w-[3px]"
            style={{
              height: `${(value / max) * height}px`,
              width: '4px'
            }}
          />
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className={`bg-card rounded-lg border border-border ${className}`}>
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center space-x-2">
            <Icon name="BarChart3" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">Regional Performance</h3>
          </div>
        </div>
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading regional data...</p>
        </div>
      </div>
    );
  }

  if (!data || data?.length === 0) {
    return (
      <div className={`bg-card rounded-lg border border-border ${className}`}>
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center space-x-2">
            <Icon name="BarChart3" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">Regional Performance</h3>
          </div>
        </div>
        <div className="p-8 text-center">
          <Icon name="Globe" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-foreground font-medium">No Geographic Data Available</p>
          <p className="text-sm text-muted-foreground mt-2">
            Geographic data will appear here once you have QR code scans with location information.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-card rounded-lg border border-border ${className}`}>
      {/* Table Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="BarChart3" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Regional Performance</h3>
        </div>
        
        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground">
            {sortedData?.length} regions
          </span>
          <Button
            variant="outline"
            size="xs"
            iconName="Download"
            iconSize={14}
          >
            Export
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-muted/20">
              <th className="text-left p-3">
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => handleSort('name')}
                  iconName={getSortIcon('name')}
                  iconPosition="right"
                  iconSize={14}
                  className="font-medium text-foreground"
                >
                  Country
                </Button>
              </th>
              <th className="text-right p-3">
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => handleSort('scans')}
                  iconName={getSortIcon('scans')}
                  iconPosition="right"
                  iconSize={14}
                  className="font-medium text-foreground"
                >
                  Scans
                </Button>
              </th>
              <th className="text-right p-3">
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => handleSort('conversions')}
                  iconName={getSortIcon('conversions')}
                  iconPosition="right"
                  iconSize={14}
                  className="font-medium text-foreground"
                >
                  Conversions
                </Button>
              </th>
              <th className="text-right p-3">
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => handleSort('conversionRate')}
                  iconName={getSortIcon('conversionRate')}
                  iconPosition="right"
                  iconSize={14}
                  className="font-medium text-foreground"
                >
                  Rate
                </Button>
              </th>
              <th className="text-right p-3">
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => handleSort('percentage')}
                  iconName={getSortIcon('percentage')}
                  iconPosition="right"
                  iconSize={14}
                  className="font-medium text-foreground"
                >
                  Share
                </Button>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData?.map((region) => (
              <tr
                key={region?.code}
                className={`border-b border-border hover:bg-muted/10 cursor-pointer transition-colors ${
                  selectedRegion?.code === region?.code ? 'bg-primary/5' : ''
                }`}
                onClick={() => {
                  setSelectedRegion(selectedRegion?.code === region?.code ? null : region);
                  onCountrySelect?.(region?.code);
                }}
              >
                <td className="p-3">
                  <div className="flex items-center space-x-3">
                    <span className="text-lg">{region?.flag}</span>
                    <div>
                      <div className="font-medium text-foreground">{region?.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {region?.code}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="p-3 text-right">
                  <div className="font-medium text-foreground">
                    {formatValue(region?.scans, 'number')}
                  </div>
                </td>
                <td className="p-3 text-right">
                  <div className="font-medium text-foreground">
                    {formatValue(region?.conversions, 'number')}
                  </div>
                </td>
                <td className="p-3 text-right">
                  <div className="font-medium text-foreground">
                    {region?.conversionRate}%
                  </div>
                </td>
                <td className="p-3 text-right">
                  <div className="font-medium text-foreground">
                    {region?.percentage}%
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RegionalPerformanceTable;